import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/lib/constants";
import Logo from "@/components/Logo";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/75 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] lg:h-[88px] flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-3 lg:gap-4 group">
          <Logo size="lg" />
          <span className="leading-tight">
            <span className="block font-display text-lg lg:text-xl tracking-tight text-white">The Phoenix</span>
            <span className="block text-[10px] tracking-[0.28em] uppercase text-amber-500 font-accent">of the Detroit Fire Department</span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm transition-colors ${
                  isActive
                    ? "text-amber-500"
                    : "text-slate-200 hover:text-amber-400"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <Link to="/donate-sponsor" className="btn-gold" data-testid="navbar-donate-btn">
            Support Phoenix
          </Link>
        </div>

        <button
          className="xl:hidden text-white p-2"
          onClick={() => setOpen((s) => !s)}
          aria-label="Toggle menu"
          data-testid="navbar-mobile-toggle"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div
          className="xl:hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          data-testid="navbar-mobile-menu"
        >
          <div className="px-5 py-4 grid grid-cols-2 gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-3 py-3 rounded-lg text-sm ${
                    isActive ? "bg-red-900/40 text-amber-400" : "text-slate-200 hover:bg-white/5"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          <div className="px-5 pb-5">
            <Link to="/donate-sponsor" className="btn-gold w-full justify-center" onClick={() => setOpen(false)} data-testid="navbar-mobile-donate-btn">
              Support Phoenix
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
